import { useEffect, useMemo, useReducer, useRef, useState } from "react";

import { Provider, ProviderStatus, ProviderType } from "@asd/customer-portal-api";
import CheckIcon from "@mui/icons-material/Check";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Checkbox, Chip, Divider, FormControlLabel, Paper, Stack, Typography } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { useSelector } from "react-redux";

import { FilterAutocomplete, FilterTextField } from "~/components/shared";
import { getScrambledName, useScrambler } from "~/providers/ScramblerProvider";
import { ProviderService } from "~/services";
import { splitCamelCase, toTitleCase } from "~/utils/stringConverters";
import { useTableExport } from "~/utils/tableExport";
import { useDashboardStaffProvider } from "./dashboard_staff/DashboardStaffProvider";

const PROVIDER_TYPE_MAP = {
  [ProviderType.DirectCareProfessional]: "Caregiver/Direct Care Professional",
  [ProviderType.Vendor]: "Vendor",
};

const columns: GridColDef[] = [
  { field: "providerName", headerName: "Name", flex: 1 },
  { field: "emailAddress", headerName: "Email Address", flex: 2 },
  {
    field: "providerType",
    headerName: "Service Provider Type",
    flex: 1,
    renderCell: (params) => toTitleCase(splitCamelCase(PROVIDER_TYPE_MAP[params.value as ProviderType])),
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 130,
    renderCell: (params) => {
      let label = params.value,
        color: "default" | "success" | "warning" | "error" = "default",
        icon: React.ReactNode,
        variant: "filled" | "outlined" = "filled";
      if (params.value === "Active") {
        color = "success";
        icon = <CheckIcon />;
      } else if (params.value === "Inactive") {
        color = "warning";
        icon = <WarningIcon />;
      } else {
        color = "error";
        icon = <PersonOffIcon />;
        variant = "outlined";
        label = "Terminated";
      }

      return <Chip color={color} icon={icon} label={label} size="small" variant={variant} />;
    },
  },
];

type TableFilters = {
  showInactive: boolean;
  providerName: string;
  providerTypes?: ProviderType[];
};

const Providers = ({ useSelectedPPTProviders }: { useSelectedPPTProviders?: boolean }) => {
  const configuration = useSelector((state: any) => state.ConfigurationSlice);

  const { isScramblerEnabled } = useScrambler();
  const { selectedParticipant } = useDashboardStaffProvider();
  const tableRef = useGridApiRef();
  const exportTable = useTableExport();

  const [isLoading, setIsLoading] = useState(false);

  const providersAbortController = useRef<AbortController>(new AbortController());
  const providersExportAbortController = useRef<AbortController>(new AbortController());

  const [tableFilters, setTableFilters] = useReducer(
    (state: TableFilters, action: { key: keyof TableFilters; value: any }) => ({
      ...state,
      [action.key]: action.value,
    }),
    {
      showInactive: false,
      providerName: "",
      providerTypes: [],
    },
  );

  const providerTypeOptions = useMemo(
    () => configuration.providerTypes.map((type: ProviderType) => PROVIDER_TYPE_MAP[type]),
    [configuration.providerTypes],
  );

  const dataSource = useMemo(
    () => ({
      getRows: async (params: any) => {
        providersAbortController.current?.abort();
        providersAbortController.current = new AbortController();

        if (!tableFilters.providerTypes?.length) {
          return {
            rows: [],
            rowCount: 0,
          };
        }

        setIsLoading(true);

        try {
          const providersResponse = await ProviderService.getProviders(
            useSelectedPPTProviders ? selectedParticipant?.participantId : undefined,
            tableFilters.showInactive ? undefined : [ProviderStatus.Active],
            configuration.providerTypes.filter((type: ProviderType) =>
              (tableFilters.providerTypes as string[]).includes(PROVIDER_TYPE_MAP[type]),
            ),
            isScramblerEnabled ? undefined : tableFilters.providerName,
            params.paginationModel.page,
            params.paginationModel.pageSize,
            params.sortModel.map(({ field, sort }: { field: string; sort: string }) => `${field}:${sort}`),
            {
              signal: providersAbortController.current?.signal,
            },
          );

          if (ProviderService.getPendingRequestCount() === 0) {
            setIsLoading(false);

            if (isScramblerEnabled) {
              (providersResponse?.data?.items ?? []).forEach((provider: Provider) => {
                provider.providerName = getScrambledName();
                provider.emailAddress = "";
              });
            }
          }

          let rows = providersResponse?.data?.items ?? [];
          let rowCount = providersResponse?.data?.total;

          if (isScramblerEnabled) {
            rows = rows.filter(({ providerName }: Provider) =>
              (providerName ?? "")
                .trim()
                .toLowerCase()
                .includes((tableFilters.providerName ?? "").trim().toLowerCase()),
            );
            rowCount = rows.length;
          }

          return {
            rows,
            rowCount,
          };
        } catch (error: any) {
          if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
            setIsLoading(false);
          }
          throw error;
        }
      },
    }),
    [tableFilters, configuration.providerTypes, useSelectedPPTProviders, isScramblerEnabled, selectedParticipant?.participantId],
  );

  useEffect(
    () => () => {
      providersAbortController.current?.abort();
      providersAbortController.current = new AbortController();
      providersExportAbortController.current?.abort();
      providersExportAbortController.current = new AbortController();
    },
    [],
  );

  return (
    <Box sx={{ p: 1, height: "100%", justifyContent: "center", alignItems: "center", overflow: "auto" }}>
      <Paper elevation={1} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Stack alignItems="start" direction="row" flexWrap="wrap" gap={1} overflow="auto" p={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={tableFilters.showInactive}
                onChange={(event) =>
                  setTableFilters({
                    key: "showInactive",
                    value: event.target.checked,
                  })
                }
                size="small"
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography>Show Inactive</Typography>}
            sx={{ mt: 0.5 }}
          />
          <Divider orientation="vertical" flexItem />
          <FilterTextField
            filterKey="providerName"
            filters={tableFilters}
            label="Search in Name"
            setFilters={setTableFilters}
            sx={{
              flexGrow: 1,
              width: 0,
              minWidth: "10rem",
            }}
          />
          <Divider orientation="vertical" flexItem />
          <FilterAutocomplete
            filterKey="providerTypes"
            filters={tableFilters}
            label="Filter by Service Provider Type"
            options={providerTypeOptions}
            setFilters={setTableFilters}
            sx={{
              flexGrow: 0,
            }}
          />
        </Stack>
        <DataGrid
          apiRef={tableRef}
          columns={columns}
          dataSource={dataSource}
          loading={isLoading}
          slotProps={{
            toolbar: {
              onExport: async (format) => {
                providersExportAbortController.current?.abort();
                providersExportAbortController.current = new AbortController();

                const data = await ProviderService.exportProviders(
                  selectedParticipant?.participantId,
                  tableFilters.showInactive ? undefined : [ProviderStatus.Active],
                  configuration.providerTypes.filter((type: ProviderType) =>
                    (tableFilters.providerTypes as string[]).includes(PROVIDER_TYPE_MAP[type]),
                  ),
                  isScramblerEnabled ? undefined : tableFilters.providerName,
                  (tableRef.current?.getSortModel() ?? []).map(({ field, sort }) => `${field}:${sort ?? "asc"}`),
                  {
                    signal: providersExportAbortController.current?.signal,
                  },
                );

                exportTable(data, "Providers", format);
              },
            },
          }}
          slots={{
            ...(!tableFilters.providerTypes?.length && {
              noRowsOverlay: () => (
                <Typography
                  sx={{
                    justifySelf: "center",
                    height: "100%",
                    alignContent: "center",
                  }}
                >
                  Please select at least one Service Provider Type
                </Typography>
              ),
            }),
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              color: (theme) => theme.palette.custom.main,
            },
            ...(isLoading && {
              ".MuiDataGrid-columnHeaders": {
                pointerEvents: "none",
              },
            }),
          }}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
};

export default Providers;
